import { z } from 'zod';
import { montarCaminho, requisitar } from './client.js';
import type { Pagina, ParametrosDePagina } from './paginacao.js';
import { paginaSchema, parametrosDePagina } from './paginacao.js';
import type { components } from './schema.js';
import { analisar } from './validacao.js';

/**
 * Categorias, produtos e saldos como a sincronização os gravou a partir do
 * ERP (r1-sync). Só leitura: o cadastro é do ERP.
 *
 * `ativo: false` é "fora de uso": excluído no ERP (com `excluidoEm`) OU
 * inativo lá (`excluidoEm` nulo). É isso que `situacao=excluidas|excluidos`
 * recorta. Sem `situacao`, a API devolve só os ativos — "todas" vai explícito.
 *
 * Quantidades vêm como texto na escala do banco (`10.000000`); ausente é
 * `null` (o ERP não informa, que não é zero).
 */

type Esquemas = components['schemas'];

const CAMINHO_CATEGORIAS = montarCaminho('/categorias');
const CAMINHO_PRODUTOS = montarCaminho('/produtos');
const CAMINHO_SALDOS = montarCaminho('/saldos');

export type CategoriaDoErp = Esquemas['CategoriaResponse'];
export type ProdutoDoErp = Esquemas['ProdutoResponse'];
export type SaldoDoErp = Esquemas['SaldoResponse'];

const quantidade = z.string();

const categoriaSchema: z.ZodType<Esquemas['CategoriaResponse']> = z.object({
  codigo: z.string(),
  nome: z.string(),
  descricao: z.string().nullable(),
  ativo: z.boolean(),
  excluidoEm: z.string().nullable(),
  /** Só os produtos ATIVOS da categoria. */
  quantidadeProdutos: z.number().int().nonnegative(),
  atualizadoEm: z.string(),
  sincronizadoEm: z.string(),
});

const produtoSchema: z.ZodType<Esquemas['ProdutoResponse']> = z.object({
  codPro: z.string(),
  codDer: z.string(),
  nome: z.string(),
  descricaoDerivacao: z.string().nullable(),
  unidadeMedida: z.string(),
  categoria: z.object({ codigo: z.string(), nome: z.string() }).nullable(),
  ativo: z.boolean(),
  controlaLote: z.boolean(),
  excluidoEm: z.string().nullable(),
  atualizadoEm: z.string(),
  sincronizadoEm: z.string(),
});

const saldoSchema: z.ZodType<Esquemas['SaldoResponse']> = z.object({
  id: z.string(),
  codFil: z.number().int(),
  codDep: z.string(),
  codPro: z.string(),
  codDer: z.string(),
  codLot: z.string(),
  numSep: z.string(),
  produtoNome: z.string(),
  quantidadeDisponivel: quantidade,
  quantidadeReservada: quantidade.nullable(),
  quantidadeBloqueada: quantidade.nullable(),
  quantidadeEmTransito: quantidade.nullable(),
  ativo: z.boolean(),
  atualizadoEm: z.string(),
  sincronizadoEm: z.string(),
  divergenciaPendenteId: z.string().nullable(),
});

const paginaDeCategoriasSchema = paginaSchema(categoriaSchema);
const paginaDeProdutosSchema = paginaSchema(produtoSchema);
const paginaDeSaldosSchema = paginaSchema(saldoSchema);

export type SituacaoDaCategoria = Esquemas['SituacaoDaCategoria'];
export type SituacaoDoCadastro = Esquemas['SituacaoDoCadastro'];

export type FiltroDeCategorias = ParametrosDePagina & {
  readonly busca?: string;
  readonly situacao?: SituacaoDaCategoria;
};

export type FiltroDeProdutos = ParametrosDePagina & {
  readonly busca?: string;
  /** Código da categoria. */
  readonly categoria?: string;
  readonly situacao?: SituacaoDoCadastro;
};

export type FiltroDeSaldos = ParametrosDePagina & {
  /** `codPro`. */
  readonly produto?: string;
  readonly codFil?: number;
  /** Código do depósito. */
  readonly deposito?: string;
  readonly situacao?: SituacaoDoCadastro;
  /** `false` é recorte de verdade: os saldos SEM divergência pendente. */
  readonly comDivergencia?: boolean;
};

/** Por nome. */
export async function listarCategorias(
  filtro: FiltroDeCategorias = {},
): Promise<Pagina<CategoriaDoErp>> {
  const corpo = await requisitar(CAMINHO_CATEGORIAS, {
    consulta: { ...parametrosDePagina(filtro), busca: filtro.busca, situacao: filtro.situacao },
  });
  return analisar(paginaDeCategoriasSchema, corpo, CAMINHO_CATEGORIAS);
}

export async function listarProdutos(filtro: FiltroDeProdutos = {}): Promise<Pagina<ProdutoDoErp>> {
  const corpo = await requisitar(CAMINHO_PRODUTOS, {
    consulta: {
      ...parametrosDePagina(filtro),
      busca: filtro.busca,
      categoria: filtro.categoria,
      situacao: filtro.situacao,
    },
  });
  return analisar(paginaDeProdutosSchema, corpo, CAMINHO_PRODUTOS);
}

/** Na ordem da chave: filial, depósito, produto, derivação, lote, série. */
export async function listarSaldos(filtro: FiltroDeSaldos = {}): Promise<Pagina<SaldoDoErp>> {
  const corpo = await requisitar(CAMINHO_SALDOS, {
    consulta: {
      ...parametrosDePagina(filtro),
      produto: filtro.produto,
      codFil: filtro.codFil,
      deposito: filtro.deposito,
      situacao: filtro.situacao,
      comDivergencia: filtro.comDivergencia,
    },
  });
  return analisar(paginaDeSaldosSchema, corpo, CAMINHO_SALDOS);
}
